import { useState } from "react";
import { NavLink } from "react-router-dom";
import { Activity, ShieldCheck, KeyRound, LayoutDashboard, Library, HelpCircle } from "lucide-react";
import { GuideDialog } from "@/components/GuideDialog";

const NavTab = ({ to, icon: Icon, children }) => (
  <NavLink
    to={to}
    end={to === "/"}
    className={({ isActive }) =>
      `flex items-center gap-2 rounded-lg px-3 py-2 mono text-xs font-medium transition-colors ${
        isActive
          ? "bg-cyan-950/40 text-cyan-300 border border-cyan-600/40"
          : "text-slate-400 hover:text-slate-200 border border-transparent"
      }`
    }
  >
    <Icon className="h-4 w-4" />
    {children}
  </NavLink>
);

export const Header = ({ quarantine, recovering, uptime, domain, pubkeys }) => {
  const gov = pubkeys?.governor_pubkey;
  const [guideOpen, setGuideOpen] = useState(false);
  return (
    <>
    <GuideDialog open={guideOpen} onOpenChange={setGuideOpen} />
    <header className="sticky top-0 z-40 border-b border-slate-800 bg-[#060A12]/85 backdrop-blur-md">
      <div className="max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="relative h-9 w-9 rounded-lg bg-gradient-to-br from-cyan-400 to-teal-600 flex items-center justify-center shadow-[0_0_18px_-2px_rgba(6,182,212,0.7)]">
            <ShieldCheck className="h-5 w-5 text-[#060A12]" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <h1 className="mono text-lg sm:text-xl font-extrabold tracking-tight text-slate-100">
              BioGovernor
            </h1>
            <p className="text-[10px] sm:text-xs text-slate-500 tracking-[0.25em] uppercase mono">
              DBCA&nbsp;Live · Deterministic Governor
            </p>
          </div>
        </div>

        <nav className="hidden md:flex items-center gap-1">
          <NavTab to="/" icon={LayoutDashboard}>Dashboard</NavTab>
          <NavTab to="/library" icon={Library}>Library</NavTab>
        </nav>

        <div className="flex items-center gap-3 sm:gap-5">
          <button
            data-testid="guide-button"
            onClick={() => setGuideOpen(true)}
            className="hidden sm:inline-flex items-center gap-1.5 rounded-lg border border-slate-800 px-3 py-1.5 mono text-[11px] text-slate-400 hover:border-cyan-600/60 hover:text-cyan-300 transition-colors"
          >
            <HelpCircle className="h-3.5 w-3.5" /> How it works
          </button>
          {gov && (
            <div className="hidden lg:flex items-center gap-2 text-[11px] mono text-slate-500">
              <KeyRound className="h-3.5 w-3.5 text-teal-400" />
              <span className="tracking-wide">Ed25519</span>
              <span className="text-slate-600">·</span>
              <span className="text-slate-400 truncate max-w-[120px]">
                {gov.slice(0, 10)}…
              </span>
            </div>
          )}
          {uptime && (
            <div className="hidden lg:flex items-center gap-2 text-[11px] mono text-slate-500">
              <Activity className="h-3.5 w-3.5 text-cyan-400" />
              <span>UPTIME</span>
              <span className="text-slate-300 tabular-nums">{uptime}</span>
            </div>
          )}
          <div
            data-testid="governor-status-beacon"
            className={`flex items-center gap-2 rounded-full border px-3 py-1.5 mono text-[11px] font-semibold tracking-wide ${
              quarantine
                ? "border-red-600/60 bg-red-950/50 text-red-300"
                : recovering
                ? "border-amber-600/60 bg-amber-950/50 text-amber-300"
                : "border-emerald-600/50 bg-emerald-950/40 text-emerald-300"
            }`}
          >
            <span
              className={`beacon h-2 w-2 rounded-full ${
                quarantine
                  ? "bg-red-400 text-red-400"
                  : recovering
                  ? "bg-amber-400 text-amber-400"
                  : "bg-emerald-400 text-emerald-400"
              }`}
            />
            {quarantine
              ? "QUARANTINE · LAMINAR HOLD"
              : recovering
              ? "RECOVERING · V(x) DESCENDING"
              : "GOVERNOR ACTIVE"}
          </div>
        </div>
      </div>
    </header>
    </>
  );
};
